import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Server, Mail, Clock } from 'lucide-react';
import { useSchema } from '../hooks/useSchema';

const PrivacyPolicy = () => {
  const lastUpdated = "March 10, 2025";
  
  // SEO Schema
  useSchema({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - CoRegulate",
    "description": "Learn how CoRegulate protects your privacy and handles your personal data.",
    "url": "https://coregulateadhd.com/privacy",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CoRegulate",
      "url": "https://coregulateadhd.com"
    }
  });
  
  return (
    <>
      <Helmet>
        <title>Privacy Policy | CoRegulate - ADHD Emotional Regulation App</title>
        <meta name="description" content="Learn how CoRegulate protects your privacy and handles your personal data. We're committed to keeping your emotional wellness journey private and secure." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/privacy" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | CoRegulate" />
        <meta property="og:description" content="Learn how CoRegulate protects your privacy and handles your personal data." />
        <meta property="og:url" content="https://coregulateadhd.com/privacy" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | CoRegulate" />
        <meta name="twitter:description" content="Learn how CoRegulate protects your privacy and handles your personal data." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-serif text-foreground mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>

          {/* Introduction */}
          <div className="prose prose-slate max-w-none">
            <div className="bg-card rounded-2xl p-8 border border-border mb-8">
              <p className="text-lg text-foreground leading-relaxed">
                At CoRegulate, we understand that your emotional wellness journey is deeply personal. 
                We're committed to protecting your privacy and ensuring that your data remains secure 
                and confidential. This policy explains how we collect, use, and safeguard your information.
              </p>
            </div>

            {/* Quick Overview */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Eye className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Your Data, Your Control</h3>
                <p className="text-sm text-muted-foreground">You can access, export, or delete your data anytime</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Lock className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">Encrypted & Secure</h3>
                <p className="text-sm text-muted-foreground">All data is encrypted in transit and at rest</p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border text-center">
                <Server className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">No Data Selling</h3>
                <p className="text-sm text-muted-foreground">We never sell your personal information</p>
              </div>
            </div>

            {/* Detailed Sections */}
            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">1</span>
                Information We Collect
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Account Information</h3>
                  <p className="text-muted-foreground">Email address and password (encrypted) when you create an account.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Wellness Data</h3>
                  <p className="text-muted-foreground">Emotional check-ins, regulation tool usage, and progress tracking data you choose to log.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Usage Information</h3>
                  <p className="text-muted-foreground">How you interact with the app to help us improve your experience.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Payment Information</h3>
                  <p className="text-muted-foreground">Processed securely through Stripe. We never store your full card details.</p>
                </div>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">2</span>
                How We Use Your Information
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Provide and personalize your regulation tools and AI companion experience</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Track your emotional wellness progress over time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Send important account notifications and updates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Improve our services based on aggregated, anonymized usage patterns</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span>Process payments and manage your subscription</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">3</span>
                Data Security
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>SSL/TLS encryption for all data transmission</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Encrypted database storage for sensitive information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Regular security audits and updates</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Secure authentication with hashed passwords</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">4</span>
                Your Rights
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">You have the right to:</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Access</strong> your personal data at any time through your account settings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Correct</strong> any inaccurate information in your profile</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Delete</strong> your account and all associated data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Export</strong> your data in a portable format</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Opt-out</strong> of marketing communications</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">5</span>
                Third-Party Services
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">We use trusted third-party services:</p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>Stripe</strong> for secure payment processing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span><strong>AI Services</strong> for personalized guidance (conversations are not stored by AI providers)</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-4 text-sm">
                  Each third-party service has their own privacy policy governing their use of data.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">6</span>
                Cookies
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground">
                  We use essential cookies to keep you logged in and remember your preferences. 
                  We do not use third-party tracking cookies or sell data to advertisers.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-serif text-foreground mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary">7</span>
                Contact Us
              </h2>
              <div className="bg-card rounded-xl p-6 border border-border">
                <p className="text-muted-foreground mb-4">
                  If you have questions about this Privacy Policy or your data, please contact us:
                </p>
                <a 
                  href="mailto:privacy@coregulateadhd.com" 
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  privacy@coregulateadhd.com
                </a>
              </div>
            </section>

            {/* Footer Note */}
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20 flex items-start gap-4">
              <Clock className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">Policy Updates</h3>
                <p className="text-muted-foreground text-sm">
                  We may update this Privacy Policy from time to time. We'll notify you of any significant 
                  changes via email or through the app. Your continued use of CoRegulate after changes 
                  constitutes acceptance of the updated policy.
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-12">
          <div className="max-w-4xl mx-auto px-4 py-8 text-center">
            <Link to="/" className="text-primary hover:underline">
              Return to CoRegulate
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicy;
